import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { arrayToObject, groupBy, last, sortBy, sum, unique, uniqueBy } from 'lib/array/arrayUtils'
import { convertTimeObjectToMinutes, convertMinutesToHoursAndMinutes, convertTimeStringToMinutes, getDisplayInfoV2, getFareClassKey, getFareClassLabel, getTotalStopCount } from 'lib/flights/flightUtils'
import { CHECKOUT_FLIGHT_FILTERS, FlightFilterType, STANDALONE_FLIGHT_FILTERS } from 'constants/flight'
import type { IntlShape } from 'react-intl'
import { getFlightFilterLabel } from 'hooks/Flights/useFlightFilterLabels'
import type { FlightsFiltersValues } from './flightFilterTypes'
import { FlightsFiltersBagType, FlightsFiltersMemberPriceType, FlightsFiltersPriceType, FlightsFiltersRecommendedType } from './flightFilterTypes'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export function getAirlineFilterItems(
  journeys: Array<App.JourneyV2>,
  options: {
    hidePrice?: boolean
  } = {},
): Array<FilterPanelCheckItem> {
  const items = journeys.map(journey => ({
    carrier: journey.carrier,
    name: journey.carrierName,
  }))

  const airlines = sortBy(
    uniqueBy(items, item => item.carrier),
    item => item.name,
    'asc',
  )

  const costs = getAirlinesCheapeastNumbers(journeys)

  const intl = getCurrentIntl()

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: 'a6FwtkwecJlLyJn60nkh',
        defaultMessage: 'All airlines',
        description: 'Flight filter option label to include all airlines',
      }),
    },
    ...airlines.map(airline => ({
      label: airline.name,
      value: airline.carrier,
      price: !options.hidePrice ? costs[airline.carrier] : undefined,
    })),
  ]
}

function getCombinedJourneyAdultCost(combinedJourney: App.FlightCombinedJourney): number | undefined {
  const departingCost = getJourneyAdultCost(combinedJourney.departingJourney)
  const returningCost = getJourneyAdultCost(combinedJourney.returningJourney)

  if (departingCost === undefined || returningCost === undefined) {
    return undefined
  }

  return departingCost + returningCost
}

function getCombinedJourneyCarrierItems(combinedJourney: App.FlightCombinedJourney) {
  return [
    {
      carrier: combinedJourney.departingJourney.carrier,
      name: combinedJourney.departingJourney.carrierName,
    },
    {
      carrier: combinedJourney.returningJourney.carrier,
      name: combinedJourney.returningJourney.carrierName,
    },
  ]
}

export function getCombinedAirlineFilterItems(
  combinedJourneys: Array<App.FlightCombinedJourney>,
  options: {
    hidePrice?: boolean
  } = {},
): Array<FilterPanelCheckItem> {
  const items = combinedJourneys.flatMap(getCombinedJourneyCarrierItems)

  const airlines = sortBy(
    uniqueBy(items, item => item.carrier),
    item => item.name,
    'asc',
  )

  const costs = getCombinedAirlinesCheapeastNumbers(combinedJourneys)
  const intl = getCurrentIntl()

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: 'a6FwtkwecJlLyJn60nkh',
        defaultMessage: 'All airlines',
        description: 'Flight filter option label to include all airlines',
      }),
    },
    ...airlines.map(airline => ({
      label: airline.name,
      value: airline.carrier,
      price: !options.hidePrice ? costs[airline.carrier] : undefined,
    })),
  ]
}

function getAirlinesCheapeastNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)

    const carrier = journey.carrier
    const costExists = cost !== undefined

    if (costExists && (cost < costs[carrier] || costs[carrier] === undefined)) {
      costs[carrier] = cost
    }

    return costs
  }, {})
}

function getCombinedAirlinesCheapeastNumbers(combinedJourneys: Array<App.FlightCombinedJourney> = []) {
  return combinedJourneys.reduce((costs: Record<string, number>, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const costExists = cost !== undefined

    if (!costExists) {
      return costs
    }

    getCombinedJourneyCarrierItems(combinedJourney).forEach(({ carrier }) => {
      if (cost < costs[carrier] || costs[carrier] === undefined) {
        costs[carrier] = cost
      }
    })

    return costs
  }, {})
}

export function getCabinTypeFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const cabinTypes = unique(journeys.map(journey => getFareClassKey(journey.flightGroup.flights[0].fareClass)))

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: '49EDkBWqTZ9xi07Lh6Oe',
        defaultMessage: 'All cabins',
        description: 'Flight filter option label to include all cabin classes',
      }),
    },
    ...cabinTypes.map(cabinType => ({
      label: getFareClassLabel(cabinType, intl),
      value: cabinType,
    })),
  ]
}

export function getStopoverDurationFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const allStopoverTimes = journeys.map(
    journey => {
      const timeSum = sum(journey.flightGroup.flights.map(flight => convertTimeObjectToMinutes(flight.stopoverTime)))
      return convertMinutesToHoursAndMinutes(timeSum)
    },
  )

  const uniqueStopOvers = uniqueBy(allStopoverTimes, m => `${m.hours}${m.minutes}`)
  const categoriedTimes = groupBy(uniqueStopOvers, time => time)

  const stopDurations = sortBy(
    categoriedTimes.keys(),
    val => convertTimeObjectToMinutes(val),
    'asc',
  )

  const intl = getCurrentIntl()
  return [
    ...stopDurations.map((duration) => {
      return {
        value: convertTimeObjectToMinutes(duration),
        label: intl.formatMessage(
          {
            id: 'v2AmNKOIy4hN0PcXbHXa',
            defaultMessage: '{hours}h {minutes}m',
            description: 'Stopover duration label in hours and minutes for flight filters',
          },
          { hours: duration.hours, minutes: duration.minutes },
        ),
      }
    }),
  ]
}

export function getStopsFilterItems(
  journeys: Array<App.JourneyV2>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const allStops = journeys.flatMap(journey => getTotalStopCount(journey.flightGroup.flights))
  const stops = unique(allStops).sort()
  const costs = getStopsCheapeastNumbers(journeys)

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: 'ASBYHvBE2dgqMwq6lXFV',
        defaultMessage: 'All stops',
        description: 'Flight filter option label to include all stop counts',
      }),
    },
    ...stops.map(stop => ({
      label: getStopsFilterLabel(stop, intl),
      value: stop.toString(),
      price: !options.hidePrice ? costs[stop] : undefined,
    })),
  ]
}

export function getCombinedStopsFilterItems(
  combinedJourneys: Array<App.FlightCombinedJourney>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const allStops = combinedJourneys.flatMap(combinedJourney => [
    getTotalStopCount(combinedJourney.departingJourney.flightGroup.flights),
    getTotalStopCount(combinedJourney.returningJourney.flightGroup.flights),
  ])
  const stops = unique(allStops).sort()
  const costs = getCombinedStopsCheapeastNumbers(combinedJourneys)

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: 'ASBYHvBE2dgqMwq6lXFV',
        defaultMessage: 'All stops',
        description: 'Flight filter option label to include all stop counts',
      }),
    },
    ...stops.map(stop => ({
      label: getStopsFilterLabel(stop, intl),
      value: stop.toString(),
      price: !options.hidePrice ? costs[stop] : undefined,
    })),
  ]
}

export function getStopoverCitiesFilterItems(
  journeys: Array<App.JourneyV2>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const cities = getJourneyStopOverCities(journeys)
  const stopOverCosts = getStopOverNumbers(journeys)
  const intl = getCurrentIntl()

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: '-1AKjuSEp9L5WYtfPlyK',
        defaultMessage: 'All stopover cities',
        description: 'Flight filter option label to include all stopover cities',
      }),
    },
    ...cities.map((city) => {
      return {
        value: city.toLowerCase(),
        label: city,
        price: !options.hidePrice ? stopOverCosts[city] : undefined,
      }
    }),
  ]
}

export function getCombinedStopoverCitiesFilterItems(
  combinedJourneys: Array<App.FlightCombinedJourney>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const cities = getJourneyStopOverCities(combinedJourneys.flatMap(combinedJourney => [
    combinedJourney.departingJourney,
    combinedJourney.returningJourney,
  ]))
  const stopOverCosts = getCombinedStopOverNumbers(combinedJourneys)
  const intl = getCurrentIntl()

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: '-1AKjuSEp9L5WYtfPlyK',
        defaultMessage: 'All stopover cities',
        description: 'Flight filter option label to include all stopover cities',
      }),
    },
    ...cities.map((city) => {
      return {
        value: city.toLowerCase(),
        label: city,
        price: !options.hidePrice ? stopOverCosts[city] : undefined,
      }
    }),
  ]
}

function getStopsCheapeastNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)

    const stopsCount = getTotalStopCount(journey.flightGroup.flights)
    const costExists = cost !== undefined

    if (costExists && (cost < costs[stopsCount] || costs[stopsCount] === undefined)) {
      costs[stopsCount] = cost
    }

    return costs
  }, {})
}

function getCombinedStopsCheapeastNumbers(combinedJourneys: Array<App.FlightCombinedJourney> = []) {
  return combinedJourneys.reduce((costs: Record<string, number>, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const departingStopsCount = getTotalStopCount(combinedJourney.departingJourney.flightGroup.flights)
    const returningStopsCount = getTotalStopCount(combinedJourney.returningJourney.flightGroup.flights)
    const costExists = cost !== undefined

    if (costExists && departingStopsCount === returningStopsCount && (cost < costs[departingStopsCount] || costs[departingStopsCount] === undefined)) {
      costs[departingStopsCount] = cost
    }

    return costs
  }, {})
}

function getStopsFilterLabel(stop: number, intl: IntlShape) {
  return stop === 0 ? intl.formatMessage({
    id: 'yny3m2nQlez2vb5blmP5',
    defaultMessage: 'No stops',
    description: 'Flight filter label for direct flights with no stops',
  }) : intl.formatMessage({
    id: 'd6HRXIL_oie6tSucCFcS',
    defaultMessage: '{count, plural, one {# stopover} other {# stopovers}}',
    description: 'Flight filter label for number of stopovers in the flights flow',
  }, { count: stop })
}

function getStopOverNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)

    if (journey.flightGroup.flights.length >= 2) {
      const stopOversFlights = journey.flightGroup.flights.slice(0, journey.flightGroup.flights.length - 1)
      const stopCities = stopOversFlights.map(f => f.arrivalCity)
      const costExists = cost !== undefined

      stopCities.forEach(city => {
        if (costExists && (cost < costs[city] || costs[city] === undefined)) {
          costs[city] = cost
        }
      })
    }

    return costs
  }, {})
}

function getCombinedStopOverNumbers(combinedJourneys: Array<App.FlightCombinedJourney> = []) {
  return combinedJourneys.reduce((costs: Record<string, number>, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const stopCities = getJourneyStopOverCities([
      combinedJourney.departingJourney,
      combinedJourney.returningJourney,
    ])
    const costExists = cost !== undefined

    stopCities.forEach(city => {
      if (costExists && (cost < costs[city] || costs[city] === undefined)) {
        costs[city] = cost
      }
    })

    return costs
  }, {})
}

function getJourneyStopOverCities(journeys: Array<App.JourneyV2>) {
  const cities: Array<string> = []
  const existingCity: Record<string, boolean> = {}

  journeys.forEach(journey => {
    if (journey.flightGroup.flights.length >= 2) {
      const stopOversFlights = journey.flightGroup.flights.slice(0, journey.flightGroup.flights.length - 1)
      stopOversFlights.forEach(flight => {
        if (!existingCity[flight.arrivalCity]) {
          cities.push(flight.arrivalCity)
          existingCity[flight.arrivalCity] = true
        }
      })
    }
  })

  return cities
}

export function getAllianceFilterItems(
  journeys: Array<App.JourneyV2>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const alliances = getJourneyAlliances(journeys)
  const alliancesCost = getAlliancesNumbers(journeys)
  const intl = getCurrentIntl()

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: 'RIiarFgTSmozcYaqk9zZ',
        defaultMessage: 'All alliances',
        description: 'Flight filter option label to include all airline alliances',
      }),
    },
    ...alliances.map((alliance) => {
      return {
        value: alliance.toLowerCase(),
        label: alliance,
        price: !options.hidePrice ? alliancesCost[alliance] : undefined,
      }
    }),
  ]
}

export function getCombinedAllianceFilterItems(
  combinedJourneys: Array<App.FlightCombinedJourney>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const alliances = getJourneyAlliances(combinedJourneys.flatMap(combinedJourney => [
    combinedJourney.departingJourney,
    combinedJourney.returningJourney,
  ]))
  const alliancesCost = getCombinedAlliancesNumbers(combinedJourneys)
  const intl = getCurrentIntl()

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: 'RIiarFgTSmozcYaqk9zZ',
        defaultMessage: 'All alliances',
        description: 'Flight filter option label to include all airline alliances',
      }),
    },
    ...alliances.map((alliance) => {
      return {
        value: alliance.toLowerCase(),
        label: alliance,
        price: !options.hidePrice ? alliancesCost[alliance] : undefined,
      }
    }),
  ]
}

function getAlliancesNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)
    const alliance = journey.flightGroup.flights[0]?.alliance

    const costExists = cost !== undefined

    if (costExists && alliance && (cost < costs[alliance] || costs[alliance] === undefined)) {
      costs[alliance] = cost
    }

    return costs
  }, {})
}

function getCombinedAlliancesNumbers(combinedJourneys: Array<App.FlightCombinedJourney> = []) {
  return combinedJourneys.reduce((costs: Record<string, number>, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const alliances = getJourneyAlliances([
      combinedJourney.departingJourney,
      combinedJourney.returningJourney,
    ])
    const costExists = cost !== undefined

    alliances.forEach(alliance => {
      if (costExists && (cost < costs[alliance] || costs[alliance] === undefined)) {
        costs[alliance] = cost
      }
    })

    return costs
  }, {})
}

function getJourneyAlliances(journeys: Array<App.JourneyV2>) {
  const alliances: Array<string> = []
  const existingAlliance: Record<string, boolean> = {}

  journeys.forEach(journey => {
    const firstFlight = journey.flightGroup.flights[0]

    if (firstFlight?.alliance && !existingAlliance[firstFlight.alliance]) {
      alliances.push(firstFlight.alliance)
      existingAlliance[firstFlight.alliance] = true
    }
  })

  return alliances
}

export function getDurationFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const allTimes = journeys.map(journey => journey.flightGroup.totalFlightDuration)
  const categoriedTimes = groupBy(allTimes, time => time)

  const stopDurations = sortBy(
    categoriedTimes.keys(),
    val => convertTimeObjectToMinutes(val),
    'asc',
  )

  const intl = getCurrentIntl()
  return [
    ...stopDurations.map((duration) => {
      return {
        value: convertTimeObjectToMinutes(duration),
        label: intl.formatMessage(
          {
            id: 'v2AmNKOIy4hN0PcXbHXa',
            defaultMessage: '{hours}h {minutes}m',
            description: 'Stopover duration label in hours and minutes for flight filters',
          },
          { hours: duration?.hours, minutes: duration?.minutes },
        ),
      }
    }),
  ]
}

export function getPricesFilterItems(
  priceType: FlightsFiltersPriceType,
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const allPrices = journeys.map(
    journey => priceType === FlightsFiltersPriceType.ADULT ? journey.price.adult.totalFare : journey.price.all.totalFare,
  )

  const categoriedPrices = unique(allPrices).filter(Boolean) as Array<number>

  const prices = sortBy(
    categoriedPrices,
    val => val,
    'asc',
  )

  return prices.map((price) => ({
    value: price,
    label: `${price}`,
  }),
  )
}

export function getDepartureTimesFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const allTimes = journeys.map(journey => journey.flightGroup.flights[0].departingTime)
  const categoriedTimes = groupBy(unique(allTimes), time => time)

  const times = sortBy(
    categoriedTimes.keys(),
    val => val,
    'asc',
  )

  return [
    ...times.map((duration) => {
      return {
        value: convertTimeStringToMinutes(duration),
        label: duration,
      }
    }),
  ]
}

export function getArrivalTimesFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const allTimes = journeys.map(journey => last(journey.flightGroup.flights).arrivalTime)
  const categoriedTimes = groupBy(unique(allTimes), time => time)

  const times = sortBy(
    categoriedTimes.keys(),
    val => val,
    'asc',
  )

  return [
    ...times.map((duration) => {
      return {
        value: convertTimeStringToMinutes(duration),
        label: duration,
      }
    }),
  ]
}

export function getRecommendedFilterItems(journeys: Array<App.JourneyV2>, options: { hidePrice?: boolean } = {}): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const costs = getRecommendedCheapeastNumbers(journeys)
  const recommendedFilterItems: Array<FilterPanelCheckItem> = []

  // Only show "Direct flights" option if there are direct flights available
  const hasDirectFlights = journeys.some(journey => journey.flightGroup.flights.length === 1)
  if (hasDirectFlights) {
    recommendedFilterItems.push({
      value: FlightsFiltersRecommendedType.DIRECT_FLIGHTS,
      label: intl.formatMessage({
        id: '_OBmhWV-IgGvFTOT7G6W',
        defaultMessage: 'Direct flights',
        description: 'Flight recommended filter label for nonstop flights',
      }),
      price: !options.hidePrice ? costs[FlightsFiltersRecommendedType.DIRECT_FLIGHTS] : undefined,
    })
  }

  recommendedFilterItems.push({
    value: FlightsFiltersRecommendedType.INCLUDES_CHECKED_BAG,
    label: intl.formatMessage({
      id: '2sdFQTkvC3jLNTaP7BBu',
      defaultMessage: 'Includes checked bag',
      description: 'Flight recommended filter label for flights with checked baggage included',
    }),
    price: !options.hidePrice ? costs[FlightsFiltersRecommendedType.INCLUDES_CHECKED_BAG] : undefined,
  })

  return recommendedFilterItems
}

export function getMemberPriceFilterItems(journeys: Array<App.JourneyV2>, options: { hidePrice?: boolean } = {}): Array<FilterPanelCheckItem> {
  const hasLuxPlusFare = journeys.some(journey => journey.bookingInfo.isLuxPlusFare)
  if (!hasLuxPlusFare) {
    return []
  }

  const intl = getCurrentIntl()
  const cheapestLuxPlusFare = getCheapestLuxPlusFare(journeys)

  return [
    {
      value: FlightsFiltersMemberPriceType.MEMBER_PRICE,
      label: intl.formatMessage({
        id: 'U7kRWQDSo1GrhtOjzdfU',
        defaultMessage: 'Member prices',
        description: 'Flight filter option label for showing only fares available with LuxPlus member pricing',
      }),
      price: !options.hidePrice ? cheapestLuxPlusFare : undefined,
    },
  ]
}

export function getBagsFilterItems(journeys: Array<App.JourneyV2>, options: { hidePrice?: boolean } = {}): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const costs = getRecommendedCheapeastNumbers(journeys)

  return [
    {
      value: FlightsFiltersBagType.CHECKED,
      label: intl.formatMessage({
        id: 'ZEzpQEN5Rtj1I2riF8Dx',
        defaultMessage: 'Includes checked bag',
        description: 'Flight bags filter label for flights with checked baggage included',
      }),
      price: !options.hidePrice ? costs[FlightsFiltersBagType.CHECKED] : undefined,
    },
  ]
}

export function getCombinedBagsFilterItems(combinedJourneys: Array<App.FlightCombinedJourney>, options: { hidePrice?: boolean } = {}): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const cheapestCheckedBagFare = getCheapestCombinedCheckedBagFare(combinedJourneys)

  return [
    {
      value: FlightsFiltersBagType.CHECKED,
      label: intl.formatMessage({
        id: 'ZEzpQEN5Rtj1I2riF8Dx',
        defaultMessage: 'Includes checked bag',
        description: 'Flight bags filter label for flights with checked baggage included',
      }),
      price: !options.hidePrice ? cheapestCheckedBagFare : undefined,
    },
  ]
}

function getRecommendedCheapeastNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)
    const existingDirectCost = costs[FlightsFiltersRecommendedType.DIRECT_FLIGHTS]
    const existingWithIncludedBaggageCost = costs[FlightsFiltersRecommendedType.INCLUDES_CHECKED_BAG]
    const existingWithCheckedCost = costs[FlightsFiltersBagType.CHECKED]

    const isDirect = journey.flightGroup.flights.length === 1
    const baggageIncluded = getDisplayInfoV2(journey.flightGroup.flights).baggageIncluded
    const costExists = cost !== undefined

    if (costExists) {
      if (isDirect && (existingDirectCost === undefined || cost < existingDirectCost)) {
        costs[FlightsFiltersRecommendedType.DIRECT_FLIGHTS] = cost
      }

      if (baggageIncluded && (existingWithIncludedBaggageCost === undefined || cost < existingWithIncludedBaggageCost)) {
        costs[FlightsFiltersRecommendedType.INCLUDES_CHECKED_BAG] = cost
      }

      const checkedBaggage = journey.flightGroup.flights[0]?.includedCheckedBaggage
      if (checkedBaggage && (existingWithCheckedCost === undefined || cost < existingWithCheckedCost)) {
        costs[FlightsFiltersBagType.CHECKED] = cost
      }
    }

    return costs
  }, {})
}

function getCheapestCombinedCheckedBagFare(combinedJourneys: Array<App.FlightCombinedJourney> = []) {
  return combinedJourneys.reduce((cheapest: number | undefined, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const includesCheckedBag = (
      isJourneyCheckedBagVisible(combinedJourney.departingJourney) &&
      isJourneyCheckedBagVisible(combinedJourney.returningJourney)
    )

    if (cost !== undefined && includesCheckedBag && (cheapest === undefined || cost < cheapest)) {
      return cost
    }

    return cheapest
  }, undefined)
}

function getJourneyAdultCost(journey: App.JourneyV2): number | undefined {
  return journey.price.adult.totalFare
}

function getCheapestLuxPlusFare(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((cheapest: number | undefined, journey) => {
    const cost = journey.bookingInfo.isLuxPlusFare ? getJourneyAdultCost(journey) : undefined

    if (cost !== undefined && (cheapest === undefined || cost < cheapest)) {
      return cost
    }

    return cheapest
  }, undefined)
}

function isJourneyMarketingAirlineVisible(journey: App.JourneyV2, airlines: Array<string>) {
  return airlines.includes(journey.carrier)
}

function isJourneyStopCountVisible(journey: App.JourneyV2, stops: Array<string>) {
  return stops.includes(getTotalStopCount(journey.flightGroup.flights).toString())
}

function getJourneyTotalLayoverMinutes(journey: App.JourneyV2) {
  return sum(journey.flightGroup.flights.map(flight => convertTimeObjectToMinutes(flight.stopoverTime)))
}

function isJourneyCheckedBagVisible(journey: App.JourneyV2) {
  return Boolean(journey.flightGroup.flights[0]?.includedCheckedBaggage)
}

function isJourneyMemberPriceVisible(journey: App.JourneyV2) {
  return Boolean(journey.bookingInfo.isLuxPlusFare)
}

function isJourneyAllianceVisible(journey: App.JourneyV2, alliances: Array<string>) {
  const flightAlliance = journey.flightGroup.flights[0]?.alliance?.toLowerCase()
  return alliances.some(alliance => alliance === flightAlliance)
}

function isJourneyStopOverCityVisible(journey: App.JourneyV2, stopOverCities: Array<string>) {
  const flights = journey.flightGroup.flights
  const flightStopOverCities = flights.slice(0, flights.length - 1).map(flight => flight.arrivalCity.toLowerCase())
  const selectedCities = arrayToObject(stopOverCities, val => val, val => val)
  return flightStopOverCities.some(city => selectedCities[city])
}

function isJourneyMinDepartureTimeVisible(journey: App.JourneyV2, minDepartureTime: string) {
  return convertTimeStringToMinutes(journey.flightGroup.flights[0].departingTime) >= parseInt(minDepartureTime)
}

function isJourneyMaxDepartureTimeVisible(journey: App.JourneyV2, maxDepartureTime: string) {
  return convertTimeStringToMinutes(journey.flightGroup.flights[0].departingTime) <= parseInt(maxDepartureTime)
}

function isJourneyMinArrivalTimeVisible(journey: App.JourneyV2, minArrivalTime: string) {
  return convertTimeStringToMinutes(last(journey.flightGroup.flights).arrivalTime) >= parseInt(minArrivalTime)
}

function isJourneyMaxArrivalTimeVisible(journey: App.JourneyV2, maxArrivalTime: string) {
  return convertTimeStringToMinutes(last(journey.flightGroup.flights).arrivalTime) <= parseInt(maxArrivalTime)
}

export function isJourneyVisible(
  journey: App.JourneyV2,
  filters: FlightsFiltersValues,
): boolean {
  let show = true

  const group = journey.flightGroup
  const flights = group.flights

  if (filters.stops?.length) {
    show = show && isJourneyStopCountVisible(journey, filters.stops)
  }

  if (filters.stopDurations) {
    const totalLayover = getJourneyTotalLayoverMinutes(journey)
    show = show && totalLayover <= parseInt(filters.stopDurations)
  }

  if (filters.airlines.length) {
    show = show && isJourneyMarketingAirlineVisible(journey, filters.airlines)
  }

  if (filters.cabinTypes.length) {
    show = show && flights.some(flight => filters.cabinTypes.includes(getFareClassKey(flight.fareClass)))
  }

  if (filters.minDepartureTime) {
    show = show && isJourneyMinDepartureTimeVisible(journey, filters.minDepartureTime)
  }

  if (filters.maxDepartureTime) {
    show = show && isJourneyMaxDepartureTimeVisible(journey, filters.maxDepartureTime)
  }

  if (filters.minArrivalTime) {
    show = show && isJourneyMinArrivalTimeVisible(journey, filters.minArrivalTime)
  }

  if (filters.maxArrivalTime) {
    show = show && isJourneyMaxArrivalTimeVisible(journey, filters.maxArrivalTime)
  }

  if (filters.recommended.length) {
    const filterDirectFlights = filters.recommended?.includes(FlightsFiltersRecommendedType.DIRECT_FLIGHTS)
    const filterCheckedBag = filters.recommended?.includes(FlightsFiltersRecommendedType.INCLUDES_CHECKED_BAG)

    if (filterDirectFlights) {
      show = show && flights.length === 1
    }

    if (filterCheckedBag) {
      const info = getDisplayInfoV2(flights)
      show = show && info.baggageIncluded
    }
  }

  if (filters.bags.length) {
    const checkedBag = filters.bags?.includes(FlightsFiltersBagType.CHECKED)

    if (checkedBag) {
      show = show && isJourneyCheckedBagVisible(journey)
    }
  }

  if (filters.memberPrices.length) {
    const filterMemberPrice = filters.memberPrices.includes(FlightsFiltersMemberPriceType.MEMBER_PRICE)

    if (filterMemberPrice) {
      show = show && isJourneyMemberPriceVisible(journey)
    }
  }

  if (filters.stopOverCities.length) {
    show = show && isJourneyStopOverCityVisible(journey, filters.stopOverCities)
  }

  if (filters.alliances.length) {
    show = show && isJourneyAllianceVisible(journey, filters.alliances)
  }

  return show
}

export function isCombinedJourneyVisible(
  combinedJourney: App.FlightCombinedJourney,
  filters: FlightsFiltersValues,
): boolean {
  let show = true

  if (filters.stops?.length) {
    show = show && (
      isJourneyStopCountVisible(combinedJourney.departingJourney, filters.stops) &&
      isJourneyStopCountVisible(combinedJourney.returningJourney, filters.stops)
    )
  }

  if (filters.airlines.length) {
    show = show && (
      isJourneyMarketingAirlineVisible(combinedJourney.departingJourney, filters.airlines) ||
      isJourneyMarketingAirlineVisible(combinedJourney.returningJourney, filters.airlines)
    )
  }

  if (filters.stopDurations) {
    const totalLayover = getJourneyTotalLayoverMinutes(combinedJourney.departingJourney) + getJourneyTotalLayoverMinutes(combinedJourney.returningJourney)
    show = show && totalLayover <= parseInt(filters.stopDurations)
  }

  if (filters.bags.length) {
    const checkedBag = filters.bags?.includes(FlightsFiltersBagType.CHECKED)

    if (checkedBag) {
      show = show && (
        isJourneyCheckedBagVisible(combinedJourney.departingJourney) &&
        isJourneyCheckedBagVisible(combinedJourney.returningJourney)
      )
    }
  }

  if (filters.memberPrices.length) {
    const filterMemberPrice = filters.memberPrices.includes(FlightsFiltersMemberPriceType.MEMBER_PRICE)

    if (filterMemberPrice) {
      show = show && (
        isJourneyMemberPriceVisible(combinedJourney.departingJourney) &&
        isJourneyMemberPriceVisible(combinedJourney.returningJourney)
      )
    }
  }

  if (filters.alliances.length) {
    show = show && (
      isJourneyAllianceVisible(combinedJourney.departingJourney, filters.alliances) ||
      isJourneyAllianceVisible(combinedJourney.returningJourney, filters.alliances)
    )
  }

  if (filters.stopOverCities.length) {
    show = show && (
      isJourneyStopOverCityVisible(combinedJourney.departingJourney, filters.stopOverCities) ||
      isJourneyStopOverCityVisible(combinedJourney.returningJourney, filters.stopOverCities)
    )
  }

  if (filters.minDepartureTime) {
    show = show && isJourneyMinDepartureTimeVisible(combinedJourney.departingJourney, filters.minDepartureTime)
  }

  if (filters.maxDepartureTime) {
    show = show && isJourneyMaxDepartureTimeVisible(combinedJourney.departingJourney, filters.maxDepartureTime)
  }

  if (filters.minArrivalTime) {
    show = show && isJourneyMinArrivalTimeVisible(combinedJourney.returningJourney, filters.minArrivalTime)
  }

  if (filters.maxArrivalTime) {
    show = show && isJourneyMaxArrivalTimeVisible(combinedJourney.returningJourney, filters.maxArrivalTime)
  }

  return show
}

export function removeUnusedFilters(activeFilters: string | Array<string>, segmentFilters: Array<string>) {
  const filtersSet = new Set(segmentFilters)
  return typeof activeFilters === 'object' ? activeFilters.filter(filter => filtersSet.has(filter)) : filtersSet.has(activeFilters)
}

export function getStandaloneMobileFilterPills(intl: IntlShape, filters: Record<string, Array<number | string> | undefined>, activeFilters: Record<string, Set<string> | undefined>) {
  const pills = STANDALONE_FLIGHT_FILTERS.filter(filter => {
    if ([FlightFilterType.TIMES, FlightFilterType.BAGS].includes(filter)) {
      return true
    }

    const activeFilter = filters[filter] || []
    const isSlider = [FlightFilterType.PRICES, FlightFilterType.DURATIONS, FlightFilterType.STOP_DURATIONS].includes(filter)
    return isSlider ? activeFilter.length > 1 : activeFilter.length > 2
  })

  return pills.map(filterKey => {
    const quantity = getFilterQuantity(activeFilters, filterKey)

    return {
      label: getFlightFilterLabel(intl, filterKey),
      value: filterKey,
      quantity,
    }
  }).sort((a, b) => b.quantity - a.quantity)
}

export function getStandaloneAllActiveFilterQuantity(activeFilters: Record<string, Set<string> | undefined>) {
  return STANDALONE_FLIGHT_FILTERS.reduce((acc, filterKey) => {
    const quantity = getFilterQuantity(activeFilters, filterKey)
    if (quantity > 0) return acc + 1
    return acc
  }, 0)
}

export function getFilterQuantity(activeFilters: Record<string, Set<string> | undefined>, filterType: FlightFilterType) {
  let quantity = 0

  if (filterType === FlightFilterType.PRICES) {
    quantity = getPricesFilterQuantity(activeFilters)
  } else if (filterType === FlightFilterType.TIMES) {
    quantity = getTimeFilterQuantity(activeFilters)
  } else {
    const currentFilter = activeFilters[filterType] || new Set([])
    quantity = [...currentFilter].filter(f => f !== 'all').length
  }

  return quantity
}

function getTimeFilterQuantity(filters: Record<string, Set<string> | undefined>) {
  const minDepartureTime = filters[FlightFilterType.MIN_DEPARTURE_TIME] || new Set([])
  const maxDepartureTime = filters[FlightFilterType.MAX_DEPARTURE_TIME] || new Set([])
  const minArrivalTime = filters[FlightFilterType.MIN_ARRIVAL_TIME] || new Set([])
  const maxArrivalTime = filters[FlightFilterType.MAX_ARRIVAL_TIME] || new Set([])

  return Math.max(minDepartureTime.size, maxDepartureTime.size) + Math.max(minArrivalTime.size, maxArrivalTime.size)
}

function getPricesFilterQuantity(filters: Record<string, Set<string> | undefined>) {
  const minPrices = filters[FlightFilterType.MIN_PRICES] || new Set([])
  const maxPrices = filters[FlightFilterType.MAX_PRICES] || new Set([])

  return Math.max(minPrices.size, maxPrices.size)
}

export function getCheckoutFilterPills(intl: IntlShape, filters: Record<string, Array<number | string> | undefined>, activeFilters: Record<string, Set<string> | undefined>) {
  const pills = CHECKOUT_FLIGHT_FILTERS.filter(filter => {
    if ([FlightFilterType.TIMES, FlightFilterType.BAGS].includes(filter)) {
      return true
    }

    const activeFilter = filters[filter] || []
    const isSlider = [FlightFilterType.PRICES, FlightFilterType.DURATIONS, FlightFilterType.STOP_DURATIONS].includes(filter)
    return isSlider ? activeFilter.length > 1 : activeFilter.length > 2
  })

  return pills.map(filterKey => {
    const quantity = getFilterQuantity(activeFilters, filterKey)

    return {
      label: getFlightFilterLabel(intl, filterKey),
      value: filterKey,
      quantity,
    }
  }).sort((a, b) => b.quantity - a.quantity)
}

export function getCheckoutAllActiveFilterQuantity(activeFilters: Record<string, Set<string> | undefined>) {
  return CHECKOUT_FLIGHT_FILTERS.reduce((acc, filterKey) => {
    const quantity = getFilterQuantity(activeFilters, filterKey)
    if (quantity > 0) return acc + 1
    return acc
  }, 0)
}
